import { useState, useCallback, useMemo, useRef } from "react";

interface IUseProgressProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  startPlayback: () => void;
  incrementPlayCount: () => void;
  paused: boolean;
}

const SECONDS = 1000;
const STEP_RATE = 0.3;
const SECONDS_PER_MINUTE = 60;
const MAX_MINUTE = 10;

export const useProgress = ({ videoRef, paused, startPlayback, incrementPlayCount }: IUseProgressProps) => {
  const [progress, setProgress] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [firstTimeEnded, setFirstTimeEnded] = useState(false);
  const seekTimeoutRef = useRef<NodeJS.Timeout>(undefined);

  const handleSeekBarChange = (time: number): void => {
    if (!Number.isFinite(time) || !videoRef.current) {
      return;
    }

    const currentTime = videoRef.current.currentTime;
    const positionSeconds = time / SECONDS;

    videoRef.current.currentTime = positionSeconds;

    setProgress(time);

    if (!paused) {
      const delay = 500;

      clearTimeout(seekTimeoutRef.current);
      seekTimeoutRef.current = setTimeout(() => {
        startPlayback();

        if (positionSeconds < currentTime) {
          // increment play count only if user seeks backwards
          incrementPlayCount();
        }
      }, delay);
    }
  };

  const handleTimeUpdate = useCallback((): void => {
    if (videoRef.current) {
      if (Math.abs(videoRef.current.currentTime - videoRef.current.duration) <= STEP_RATE) {
        if (!firstTimeEnded) {
          setFirstTimeEnded(true);
        }

        // increment play count when video ended
        incrementPlayCount();
      }
      setProgress(videoRef.current.currentTime * SECONDS);
    }
  }, [firstTimeEnded, videoRef]);

  const handleLoadedMetadata = useCallback((): void => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration * SECONDS);
    }
  }, [videoRef]);

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / SECONDS_PER_MINUTE);
    const seconds = Math.floor(time % SECONDS_PER_MINUTE);
    return `${minutes < MAX_MINUTE ? "0" : ""}${minutes}:${seconds < MAX_MINUTE ? "0" : ""}${seconds}`;
  }, []);

  const curTimeFormatted = useMemo(() => {
    if (!videoRef.current) {
      return "00:00";
    }
    const remainingTime = (duration - progress);

    if (remainingTime < 0) {
      return "00:00";
    }

    return formatTime(remainingTime / SECONDS);
  }, [videoRef, formatTime, progress, duration]);

  return {
    progress,
    duration,
    firstTimeEnded,
    handleSeekBarChange,
    handleTimeUpdate,
    handleLoadedMetadata,
    curTimeFormatted
  };
};
